import { useEffect, useRef } from "react";
import { clearUserScopedStorage } from "@/lib/userScopedStorage";

/**
 * Clears user-scoped localStorage exactly once when the authenticated user
 * transitions from non-null to null (session expiry / logout detected by the
 * auth query resolving to 401→null).
 *
 * Under real network conditions many in-flight requests can fail with 401 at
 * once and the auth query may refire several times while still returning
 * null. Because the previous-user ref is reset to null after the first sweep,
 * subsequent renders with a null user are no-ops — the sweep fires exactly
 * once per expiry.
 *
 * Renders while auth is still loading are ignored so a transient
 * "no answer yet" state can never be mistaken for a logout.
 */
export function useSessionExpiryStorageSweep(
  user: { id: string } | null | undefined,
  isLoading: boolean,
): void {
  const prevUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isLoading) return;
    const prevId = prevUserIdRef.current;
    const currentId = user?.id ?? null;
    if (prevId && !currentId) {
      clearUserScopedStorage(prevId);
    }
    prevUserIdRef.current = currentId;
  }, [user, isLoading]);
}
